import { Info, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
    RecalculateAllMissingTrialResultsDocument,
    RecalculateSingleTrialResultDocument,
    RegenerateGameEvalsDocument,
    RegenerateTournamentEvalsDocument,
    TroubleshootingStatsDocument,
} from "@/gen/graphql";

import { IssueDisplayWithData } from "./components/IssueDisplayWithData";
import { SyncerControl } from "./components/SyncerControl";

const TroubleshootingRoute = () => {
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [isRecalculatingSingle, setIsRecalculatingSingle] = useState(false);
    const [isRegeneratingEvals, setIsRegeneratingEvals] = useState(false);
    const [gameIdInput, setGameIdInput] = useState("");
    const [tournamentIdInput, setTournamentIdInput] = useState("");
    const [trialIdInput, setTrialIdInput] = useState("");

    const [{ data, fetching, error }, refetch] = useQuery({
        query: TroubleshootingStatsDocument,
    });

    const [_recalculateResult, recalculateAllMissingTrialResults] = useMutation(
        RecalculateAllMissingTrialResultsDocument
    );

    const [_recalculateSingleResult, recalculateSingleTrialResult] = useMutation(
        RecalculateSingleTrialResultDocument
    );

    const [_regenerateGameEvalsResult, regenerateGameEvals] = useMutation(
        RegenerateGameEvalsDocument
    );

    const [_regenerateTournamentEvalsResult, regenerateTournamentEvals] = useMutation(
        RegenerateTournamentEvalsDocument
    );

    const handleRecalculateAllMissingTrialResults = async () => {
        const confirmed = window.confirm(
            `This will recalculate trial results for ALL trials that have a game with a result but no trial result. Are you sure?`
        );
        if (!confirmed) return;

        setIsRecalculating(true);
        try {
            const res = await recalculateAllMissingTrialResults({});

            if (res.error) {
                toast.error("Failed to recalculate trial results", {
                    description: res.error.message,
                });
            } else if (res.data?.recalculateAllMissingTrialResults) {
                const { totalTrialsChecked, trialsUpdated, trialsFailed } =
                    res.data.recalculateAllMissingTrialResults;
                toast.success(`Recalculation complete`, {
                    description: `Checked: ${totalTrialsChecked}, Updated: ${trialsUpdated}, Failed: ${trialsFailed}`,
                });
            }
        } finally {
            setIsRecalculating(false);
        }
    };

    const handleRecalculateSingleTrialResult = async () => {
        if (!trialIdInput.trim()) {
            toast.error("Please enter a trial ID");
            return;
        }

        const confirmed = window.confirm(
            `This will recalculate the result for trial ${trialIdInput}. Are you sure?`
        );
        if (!confirmed) return;

        setIsRecalculatingSingle(true);
        try {
            const res = await recalculateSingleTrialResult({ trialId: trialIdInput });

            if (res.error) {
                toast.error("Failed to recalculate trial result", {
                    description: res.error.message,
                });
            } else if (res.data?.recalculateSingleTrialResult) {
                const { success } = res.data.recalculateSingleTrialResult;
                if (success) {
                    toast.success("Trial result recalculated successfully");
                    setTrialIdInput("");
                    refetch();
                } else {
                    toast.info("No changes made - trial may not be complete yet");
                }
            }
        } finally {
            setIsRecalculatingSingle(false);
        }
    };

    const handleRegenerateGameEvals = async () => {
        if (!gameIdInput.trim()) {
            toast.error("Please enter a game ID");
            return;
        }

        const confirmed = window.confirm(
            `This will regenerate evaluations for game ${gameIdInput}. Are you sure?`
        );
        if (!confirmed) return;

        setIsRegeneratingEvals(true);
        try {
            const res = await regenerateGameEvals({ gameId: gameIdInput });

            if (res.error) {
                toast.error("Failed to regenerate game evaluations", {
                    description: res.error.message,
                });
            } else if (res.data?.regenerateGameEvals) {
                const { success, message } = res.data.regenerateGameEvals;
                if (success) {
                    toast.success("Evaluation regeneration started", {
                        description: message,
                    });
                    setGameIdInput("");
                } else {
                    toast.error("Failed to regenerate evaluations", {
                        description: message,
                    });
                }
            }
        } finally {
            setIsRegeneratingEvals(false);
        }
    };

    const handleRegenerateTournamentEvals = async () => {
        if (!tournamentIdInput.trim()) {
            toast.error("Please enter a tournament ID");
            return;
        }

        const confirmed = window.confirm(
            `This will regenerate evaluations for all games with missing evals in tournament ${tournamentIdInput}. Are you sure?`
        );
        if (!confirmed) return;

        setIsRegeneratingEvals(true);
        try {
            const res = await regenerateTournamentEvals({
                tournamentId: tournamentIdInput,
                limit: null,
            });

            if (res.error) {
                toast.error("Failed to regenerate tournament evaluations", {
                    description: res.error.message,
                });
            } else if (res.data?.regenerateTournamentEvals) {
                const { gamesProcessed, gamesWithMissingEvals, message } =
                    res.data.regenerateTournamentEvals;
                toast.success("Tournament evaluation regeneration started", {
                    description: `${message}. Found ${gamesWithMissingEvals} games with missing evals, processing ${gamesProcessed}.`,
                });
                setTournamentIdInput("");
            }
        } finally {
            setIsRegeneratingEvals(false);
        }
    };

    if (fetching) {
        return <FullPageSpinner />;
    }

    if (error) {
        return (
            <div className="m-4">
                <Card className="border-red-200 bg-red-50">
                    <CardHeader>
                        <CardTitle className="text-red-800">
                            Error Loading Troubleshooting Stats
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-red-700">{error.message}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const stats = data?.troubleshootingStats;

    return (
        <div className="m-4 space-y-4">
            <CardTitle className="mt-8">Troubleshooting Dashboard</CardTitle>

            <div className="grid gap-4 md:grid-cols-2">
                <div className="col-span-full">
                    <SyncerControl />
                </div>

                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CardTitle className="text-lg">Missing Trial Results</CardTitle>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        Trials where all games have finished (have results) but the
                                        trial result is missing. These can be recalculated using the
                                        trial format.
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <Badge
                                variant={
                                    stats?.missingTrialResults?.length ? "destructive" : "secondary"
                                }
                            >
                                {stats?.missingTrialResults?.length || 0}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={handleRecalculateAllMissingTrialResults}
                                    disabled={
                                        isRecalculating || !stats?.missingTrialResults?.length
                                    }
                                    size="sm"
                                    variant="outline"
                                >
                                    {isRecalculating ? (
                                        <>
                                            <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                            Recalculating...
                                        </>
                                    ) : (
                                        "Recalculate all missing trial results"
                                    )}
                                </Button>
                            </div>

                            {stats?.missingTrialResults && stats.missingTrialResults.length > 0 && (
                                <div>
                                    <p className="text-muted-foreground mb-2 text-sm">
                                        {stats.missingTrialResults.length} issues:
                                    </p>
                                    <ScrollArea className="h-[600px] w-full rounded-md border">
                                        <div className="space-y-3 p-4">
                                            {stats.missingTrialResults.map(issue => (
                                                <IssueDisplayWithData
                                                    key={issue.trialId}
                                                    issue={issue}
                                                    type="missing"
                                                />
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CardTitle className="text-lg">Stalled Games</CardTitle>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        Games or trials that haven't finished when they should have.
                                        This includes games without results when the next round has
                                        started or when the tournament has ended.
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <Badge
                                variant={stats?.stalledGames?.length ? "destructive" : "secondary"}
                            >
                                {stats?.stalledGames?.length || 0}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-col gap-4">
                            <p className="text-muted-foreground text-sm">
                                These games require manual intervention to either set a result or
                                delete the game.
                            </p>

                            {stats?.stalledGames && stats.stalledGames.length > 0 && (
                                <div>
                                    <p className="text-muted-foreground mb-2 text-sm">
                                        {stats.stalledGames.length} issues:
                                    </p>
                                    <ScrollArea className="h-[600px] w-full rounded-md border">
                                        <div className="space-y-3 p-4">
                                            {stats.stalledGames.map(issue => (
                                                <IssueDisplayWithData
                                                    key={`${issue.trialId}-${issue.gameId || "no-game"}`}
                                                    issue={issue}
                                                    type="stalled"
                                                />
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="col-span-full">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CardTitle className="text-lg">
                                    Recalculate Single Trial Result
                                </CardTitle>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        Manually recalculate the result for a specific trial. This
                                        will check if all required games have been played and set
                                        the trial result according to the format.
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="trial-id">Trial ID</Label>
                                <Input
                                    id="trial-id"
                                    placeholder="Enter trial UUID"
                                    value={trialIdInput}
                                    onChange={e => setTrialIdInput(e.target.value)}
                                    disabled={isRecalculatingSingle}
                                />
                            </div>
                            <Button
                                onClick={handleRecalculateSingleTrialResult}
                                disabled={isRecalculatingSingle || !trialIdInput.trim()}
                                size="sm"
                                variant="outline"
                            >
                                {isRecalculatingSingle ? (
                                    <>
                                        <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                        Recalculating...
                                    </>
                                ) : (
                                    "Recalculate trial result"
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="col-span-full">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CardTitle className="text-lg">Regenerate missing evals</CardTitle>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Info
                                            size={16}
                                            className="text-muted-foreground cursor-pointer"
                                        />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                        Regenerate chess position evaluations for games that are
                                        missing them. You can regenerate evals for a single game or
                                        for all games in a tournament.
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-6 md:grid-cols-2">
                            <div className="space-y-4">
                                <div>
                                    <h3 className="mb-2 text-sm font-medium">Single game</h3>
                                    <div className="space-y-2">
                                        <div>
                                            <Label htmlFor="game-id">Game ID</Label>
                                            <Input
                                                id="game-id"
                                                placeholder="Enter game UUID"
                                                value={gameIdInput}
                                                onChange={e => setGameIdInput(e.target.value)}
                                                disabled={isRegeneratingEvals}
                                            />
                                        </div>
                                        <Button
                                            onClick={handleRegenerateGameEvals}
                                            disabled={isRegeneratingEvals || !gameIdInput.trim()}
                                            size="sm"
                                            variant="outline"
                                        >
                                            {isRegeneratingEvals ? (
                                                <>
                                                    <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                                    Regenerating...
                                                </>
                                            ) : (
                                                "Regenerate game evals"
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="mb-2 text-sm font-medium">Tournament</h3>
                                    <div className="space-y-2">
                                        <div>
                                            <Label htmlFor="tournament-id">Tournament ID</Label>
                                            <Input
                                                id="tournament-id"
                                                placeholder="Enter tournament UUID"
                                                value={tournamentIdInput}
                                                onChange={e => setTournamentIdInput(e.target.value)}
                                                disabled={isRegeneratingEvals}
                                            />
                                        </div>
                                        <Button
                                            onClick={handleRegenerateTournamentEvals}
                                            disabled={
                                                isRegeneratingEvals || !tournamentIdInput.trim()
                                            }
                                            size="sm"
                                            variant="outline"
                                        >
                                            {isRegeneratingEvals ? (
                                                <>
                                                    <RefreshCcw className="mr-2 h-4 w-4 animate-spin" />
                                                    Regenerating...
                                                </>
                                            ) : (
                                                "Regenerate tournament evals"
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default TroubleshootingRoute;
